import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Filter, Search, Grid, List, SlidersHorizontal } from 'lucide-react';
import axios from 'axios';
import ProductCard from './ProductCard';
import { Button } from './ui/button';
import { observeNewElements } from '../utils/scrollAnimations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoryPage = ({ addToCart }) => {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [brands, setBrands] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchBrands();
  }, [category]);

  useEffect(() => {
    fetchProducts();
  }, [searchTerm, selectedBrand, sortBy]);

  // Reinitialize scroll animations when products change
  useEffect(() => {
    if (products.length > 0) {
      const timer = setTimeout(() => {
        observeNewElements();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (category) params.append('category', category);
      if (searchTerm) params.append('search', searchTerm);
      if (selectedBrand) params.append('brand', selectedBrand);
      params.append('limit', '1000'); // Fetch all products
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      let sortedProducts = response.data;
      
      // Client-side sorting
      switch (sortBy) {
        case 'price_low':
          sortedProducts.sort((a, b) => a.price - b.price);
          break;
        case 'price_high':
          sortedProducts.sort((a, b) => b.price - a.price);
          break;
        case 'name':
          sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
          break;
        default:
          break;
      }
      
      setProducts(sortedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const getCategoryTitle = () => {
    const titles = {
      basketball: 'Basketball Sneakers',
      running: 'Running Shoes',
      lifestyle: 'Lifestyle Sneakers',
      limited_edition: 'Limited Edition Collection'
    };
    return titles[category] || 'All Sneakers';
  };

  const getCategoryDescription = () => {
    const descriptions = {
      basketball: 'Performance-driven basketball shoes designed for the court',
      running: 'Engineered for speed, comfort, and endurance',
      lifestyle: 'Stylish sneakers perfect for everyday wear',
      limited_edition: 'Exclusive and rare releases from top brands'
    };
    return descriptions[category] || 'Discover our complete sneaker collection';
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBrand('');
    setSortBy('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center" data-testid="loading-indicator">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid={`category-page-${category}`}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="category-title">
              {getCategoryTitle()}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-testid="category-description">
              {getCategoryDescription()}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search sneakers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              {/* Brand Filter */}
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                data-testid="brand-filter"
              >
                <option value="">All Brands</option>
                {brands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                data-testid="sort-select"
              >
                <option value="" disabled>Sort By:</option>
                <option value="name">A-Z</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
              </select>

              {/* Clear Filters */}
              {(searchTerm || selectedBrand || sortBy) && (
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  size="sm"
                  data-testid="clear-filters"
                >
                  Clear Filters
                </Button>
              )}

              {/* View Mode Toggle */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-brand-purple text-white' : 'bg-white text-gray-600'}`}
                  data-testid="grid-view-button"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-brand-purple text-white' : 'bg-white text-gray-600'}`}
                  data-testid="list-view-button"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" data-testid="results-count">
              {products.length} {products.length === 1 ? 'Product' : 'Products'} Found
            </h2>
            {searchTerm && (
              <p className="text-gray-600 mt-1" data-testid="search-results-info">
                Showing results for "{searchTerm}"
              </p>
            )}
          </div>
        </div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <div
            className={`${
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                : 'space-y-4'
            }`}
            data-testid="products-container"
          >
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={addToCart}
                compact={viewMode === 'list'}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16" data-testid="no-products-found">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">No products found</h3>
              <p className="text-gray-500 mb-6">
                Try adjusting your filters or search terms to find what you're looking for.
              </p>
              <Button onClick={clearFilters} className="bg-brand-purple hover:bg-brand-purple-dark text-white">
                Clear All Filters
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;